import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { isWithinPeriod, useAnalyticsPeriod } from '../lib/analytics-period'

interface UserFeedback {
  id: string
  feedback_type: string
  message: string
  user_name: string | null
  user_email: string | null
  page_url: string | null
  user_agent: string | null
  screen_width: number | null
  screen_height: number | null
  /** 1-5 from the exit survey or Help Bot form. NULL means "not asked", NOT a score of zero. */
  rating: number | null
  status: string
  team_notes: string | null
  jira_issue_key: string | null
  created_at: string
  updated_at: string
}

type StatusFilter = 'all' | 'new' | 'in_progress' | 'resolved' | 'closed'
type TypeFilter = 'all' | 'bug' | 'suggestion' | 'question' | 'general' | 'exit' | 'helpbot'

const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/feedback`
  : '/api/feedback'

export default function Feedback() {
  const { period } = useAnalyticsPeriod()
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  const [selectedFeedback, setSelectedFeedback] = useState<UserFeedback | null>(null)
  const [teamNotes, setTeamNotes] = useState('')

  // Fetch feedback
  const { data: feedback = [], isLoading, error, refetch } = useQuery<UserFeedback[]>({
    queryKey: ['feedback', statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (statusFilter !== 'all') params.set('status', statusFilter)

      const response = await fetch(`${API_BASE}?${params.toString()}`)
      if (!response.ok) throw new Error('Failed to fetch feedback')

      const data = await response.json()
      // Backend returns array directly, not wrapped in { feedback: [...] }
      return Array.isArray(data) ? data : []
    },
    staleTime: 30000,
  })

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const response = await fetch(`${API_BASE}/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      })
      if (!response.ok) throw new Error('Failed to update status')
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback'] })
    },
  })

  // Update notes mutation
  const updateNotesMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      const response = await fetch(`${API_BASE}/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ team_notes: notes }),
      })
      if (!response.ok) throw new Error('Failed to save notes')
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback'] })
      if (selectedFeedback) {
        setSelectedFeedback({ ...selectedFeedback, team_notes: teamNotes })
      }
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`${API_BASE}/${id}`, { method: 'DELETE' })
      if (!response.ok) throw new Error('Failed to delete feedback')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback'] })
      setSelectedFeedback(null)
    },
  })

  const handleStatusChange = (id: string, newStatus: string) => {
    // Optimistically update the selected feedback
    if (selectedFeedback) {
      setSelectedFeedback({ ...selectedFeedback, status: newStatus })
    }
    updateStatusMutation.mutate({ id, status: newStatus })
  }

  const handleSaveNotes = () => {
    if (!selectedFeedback) return
    updateNotesMutation.mutate({ id: selectedFeedback.id, notes: teamNotes })
  }

  const handleDelete = (id: string) => {
    if (!confirm('Are you sure you want to delete this feedback?')) return
    deleteMutation.mutate(id)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'bug': return '🐛'
      case 'suggestion': return '💡'
      case 'question': return '❓'
      case 'exit': return '👋'
      case 'helpbot': return '🤖'
      default: return '💬'
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new': return 'badge-info'
      case 'in_progress': return 'badge-warning'
      case 'resolved': return 'badge-success'
      case 'closed': return 'badge-neutral'
      default: return 'badge-ghost'
    }
  }

  const filteredFeedback = feedback.filter((f) => {
    if (!isWithinPeriod(f.created_at, period)) return false
    if (typeFilter !== 'all' && f.feedback_type !== typeFilter) return false
    return true
  })

  if (error) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <div className="card bg-base-100 shadow-xl max-w-md border border-base-300">
          <div className="card-body items-center text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-error mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="card-title text-base-content">Failed to load feedback</h2>
            <p className="text-base-content opacity-80">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
            <div className="card-actions mt-4">
              <button className="btn btn-primary" onClick={() => refetch()}>
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="page-title">User Feedback</h1>
          <p className="text-sm opacity-80 mt-1">{filteredFeedback.length} feedback items</p>
        </div>
        <button className="btn btn-primary" onClick={() => refetch()}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">Status</span>
          </label>
          <select
            className="select select-bordered"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <option value="all">All</option>
            <option value="new">New</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">Type</span>
          </label>
          <select
            className="select select-bordered"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
          >
            <option value="all">All Types</option>
            <option value="bug">Bug Reports</option>
            <option value="suggestion">Suggestions</option>
            <option value="question">Questions</option>
            <option value="general">General</option>
            <option value="exit">Exit Survey</option>
            <option value="helpbot">Help Bot</option>
          </select>
        </div>
      </div>

      {/* Feedback Table */}
      <div className="card bg-base-100 shadow-xl overflow-hidden border border-base-300">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Status</th>
                <th>Jira</th>
                <th>Message</th>
                <th>User</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <span className="loading loading-spinner loading-lg"></span>
                  </td>
                </tr>
              )}
              {!isLoading && filteredFeedback.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-12 opacity-60">
                    No feedback items found
                  </td>
                </tr>
              )}
              {!isLoading && filteredFeedback.map((item) => (
                <tr
                  key={item.id}
                  className={`cursor-pointer hover ${selectedFeedback?.id === item.id ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedFeedback(item)
                    setTeamNotes(item.team_notes || '')
                  }}
                >
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getTypeIcon(item.feedback_type)}</span>
                      <span className="capitalize">{item.feedback_type}</span>
                      {/* Only the exit survey and Help Bot form carry a score; everything else is null. */}
                      {item.rating !== null && (
                        <span className="badge badge-sm badge-outline whitespace-nowrap">
                          {item.rating}/5
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${getStatusBadge(item.status)}`}>
                      {item.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td>
                    {item.jira_issue_key ? (
                      <a
                        href={`https://asmjira.atlassian.net/browse/${item.jira_issue_key}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link link-primary text-sm flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {item.jira_issue_key}
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    ) : (
                      <span className="text-xs opacity-40">-</span>
                    )}
                  </td>
                  <td className="max-w-md truncate">{item.message}</td>
                  <td>
                    <div className="text-sm">
                      <div>{item.user_name || 'Anonymous'}</div>
                      {item.user_email && (
                        <div className="text-xs opacity-60">{item.user_email}</div>
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap">{formatDate(item.created_at)}</td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedFeedback(item)
                          setTeamNotes(item.team_notes || '')
                        }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button
                        className="btn btn-ghost btn-sm text-error"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDelete(item.id)
                        }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedFeedback && (
        <dialog className="modal modal-open">
          <div className="modal-box max-w-3xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-start justify-between mb-6 pb-4 border-b">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{getTypeIcon(selectedFeedback.feedback_type)}</span>
                <div>
                  <h3 className="text-lg font-bold capitalize">{selectedFeedback.feedback_type} Report</h3>
                  <p className="text-sm opacity-60">
                    {formatDate(selectedFeedback.created_at)}
                    {selectedFeedback.rating !== null && (
                      <> · rated {selectedFeedback.rating}/5</>
                    )}
                  </p>
                </div>
              </div>
              <button
                className="btn btn-ghost btn-sm btn-circle"
                onClick={() => setSelectedFeedback(null)}
              >
                ✕
              </button>
            </div>

            <div className="space-y-6">
              {/* Status & Jira Row */}
              <div className="grid grid-cols-2 gap-4">
                {/* Status */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">Status</span>
                    {updateStatusMutation.isPending && (
                      <span className="label-text-alt text-info">Updating...</span>
                    )}
                  </label>
                  <select
                    className="select select-bordered w-full"
                    value={selectedFeedback.status}
                    onChange={(e) => handleStatusChange(selectedFeedback.id, e.target.value)}
                    disabled={updateStatusMutation.isPending}
                  >
                    <option value="new">🆕 New</option>
                    <option value="in_progress">⏳ In Progress</option>
                    <option value="resolved">✅ Resolved</option>
                    <option value="closed">🔒 Closed</option>
                  </select>
                </div>

                {/* Jira Issue */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">Jira Issue</span>
                  </label>
                  {selectedFeedback.jira_issue_key ? (
                    <a
                      href={`https://asmjira.atlassian.net/browse/${selectedFeedback.jira_issue_key}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline btn-info btn-sm flex items-center gap-2"
                    >
                      <span>{selectedFeedback.jira_issue_key}</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  ) : (
                    <div className="text-sm opacity-50 py-3">No Jira issue created</div>
                  )}
                </div>
              </div>

              {/* User Info */}
              <div className="card bg-base-200">
                <div className="card-body p-4">
                  <h4 className="font-semibold text-sm mb-2">User Information</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="opacity-60">Name:</span>
                      <span className="ml-2 font-medium">{selectedFeedback.user_name || 'Anonymous'}</span>
                    </div>
                    <div>
                      <span className="opacity-60">Email:</span>
                      <span className="ml-2">
                        {selectedFeedback.user_email ? (
                          <a href={`mailto:${selectedFeedback.user_email}`} className="link link-primary">
                            {selectedFeedback.user_email}
                          </a>
                        ) : (
                          <span className="opacity-50">Not provided</span>
                        )}
                      </span>
                    </div>
                    {selectedFeedback.screen_width && selectedFeedback.screen_height && (
                      <div>
                        <span className="opacity-60">Screen:</span>
                        <span className="ml-2 font-medium">
                          {selectedFeedback.screen_width} × {selectedFeedback.screen_height}
                        </span>
                      </div>
                    )}
                    {selectedFeedback.page_url && (
                      <div className="col-span-2">
                        <span className="opacity-60">Page:</span>
                        <a
                          href={selectedFeedback.page_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 link link-primary text-xs break-all"
                        >
                          {selectedFeedback.page_url}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="label">
                  <span className="label-text font-medium">Feedback Message</span>
                </label>
                <div className="card bg-base-200">
                  <div className="card-body p-4 max-h-64 overflow-y-auto">
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">
                      {selectedFeedback.message}
                    </p>
                  </div>
                </div>
              </div>

              {/* Team Notes */}
              <div>
                <label className="label">
                  <span className="label-text font-medium">Internal Team Notes</span>
                  <span className="label-text-alt opacity-60">Only visible to team</span>
                </label>
                <textarea
                  className="textarea textarea-bordered w-full min-h-[120px]"
                  value={teamNotes}
                  onChange={(e) => setTeamNotes(e.target.value)}
                  placeholder="Add internal notes about this feedback..."
                />
                <div className="flex justify-end mt-2">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleSaveNotes}
                    disabled={updateNotesMutation.isPending || teamNotes === (selectedFeedback.team_notes || '')}
                  >
                    {updateNotesMutation.isPending ? (
                      <>
                        <span className="loading loading-spinner loading-xs"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Save Notes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="modal-action border-t pt-4 mt-6">
              <button
                className="btn btn-error btn-sm"
                onClick={() => handleDelete(selectedFeedback.id)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete Feedback
              </button>
              <button
                className="btn btn-sm"
                onClick={() => setSelectedFeedback(null)}
              >
                Close
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop" onClick={() => setSelectedFeedback(null)}>
            <button>close</button>
          </form>
        </dialog>
      )}
    </div>
  )
}
